import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Linkedin,
  Facebook,
  Instagram,
  CheckCircle2,
} from 'lucide-react';
import { services } from '../data/mock';
import { useT } from '../i18n/LanguageContext';

const heroBg =
  'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxlbGVjdHJpY2FsJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc4NDgxNjg0Mnww&ixlib=rb-4.1.0&q=85';

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { lang, t } = useT();
  const c = t.contact;

  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  /*
   * Anti-spam:
   * Record when the form was opened.
   * The server will reject unrealistically fast submissions.
   */
  const [formStartedAt] = useState(() => Date.now());

  /*
   * Invisible honeypot.
   * Real visitors never fill this field.
   * Bots that automatically fill every input are caught by the server.
   */
  const [website, setWebsite] = useState('');

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();

    if (sending) {
      return;
    }

    setSending(true);
    setSent(false);
    setError('');

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...form,

          /*
           * Anti-spam values.
           */
          website,
          formStartedAt,

          language:
            lang ||
            document.documentElement.lang ||
            'fr',
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            'Unable to send your message.'
        );
      }

      /*
       * Redirect only after the server confirms
       * that Mailgun accepted the enquiry.
       */
      window.location.href = '/thank-you';

    } catch (err) {
      console.error(
        'Contact form submission failed:',
        err
      );

      const errorMessage =
        lang === 'en'
          ? 'Your message could not be sent. Please try again.'
          : lang === 'ar'
            ? 'تعذر إرسال رسالتك. يرجى المحاولة مرة أخرى.'
            : 'Votre message n’a pas pu être envoyé. Veuillez réessayer.';

      setError(errorMessage);

    } finally {
      setSending(false);
    }
  };

  const homeLabel =
    t.nav?.home ||
    (lang === 'en'
      ? 'Home'
      : lang === 'ar'
        ? 'الرئيسية'
        : 'Accueil');

  const contactLabel =
    t.nav?.contact ||
    (lang === 'en'
      ? 'Contact'
      : lang === 'ar'
        ? 'اتصل بنا'
        : 'Contact');

  const sendingLabel =
    lang === 'en'
      ? 'Sending…'
      : lang === 'ar'
        ? 'جارٍ الإرسال…'
        : 'Envoi en cours…';

  const namePlaceholder =
    lang === 'en'
      ? 'John Doe'
      : lang === 'ar'
        ? 'الاسم الكامل'
        : 'Jean Dupont';

  const emailPlaceholder =
    lang === 'en'
      ? 'john@example.com'
      : lang === 'ar'
        ? 'john@example.com'
        : 'jean@exemple.fr';

  const phonePlaceholder =
    lang === 'en'
      ? '+33 6 XX XX XX XX'
      : lang === 'ar'
        ? '+33 6 XX XX XX XX'
        : '+33 6 XX XX XX XX';

  const companyPlaceholder =
    lang === 'en'
      ? 'Your company'
      : lang === 'ar'
        ? 'اسم الشركة'
        : 'Votre entreprise';

  return (
    <main
      className="bg-white"
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* HERO */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
        <img
          src={heroBg}
          alt="Contact GPE"
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/85 to-[#0b1c3a]/95" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <span className="inline-block px-3 py-1 rounded-full bg-[#f5c518]/15 border border-[#f5c518]/40 text-[#f5c518] text-[11px] tracking-[0.25em] font-semibold">
            {c.pill}
          </span>

          <h1 className="mt-6 text-white font-black leading-[1.05] tracking-tight text-5xl md:text-6xl lg:text-7xl max-w-4xl">
            {c.titleA}{' '}
            <span className="italic font-serif text-[#f5c518]">
              {c.titleAccent}
            </span>
          </h1>

          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {c.subtitle}
          </p>

          <nav
            aria-label="breadcrumb"
            className="mt-8 flex items-center gap-2 text-white/70 text-sm"
          >
            <Link
              to="/"
              className="hover:text-white transition-colors"
            >
              {homeLabel}
            </Link>

            <ChevronRight className="w-3.5 h-3.5" />

            <span className="text-[#f5c518] font-semibold">
              {contactLabel}
            </span>
          </nav>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" />
      </section>

      {/* MAIN GRID */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[380px_1fr] gap-8 items-start">

          {/* LEFT: CONTACT INFO */}
          <div className="space-y-4">

            {/* PHONE */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" />
                </span>

                <div>
                  <div className="text-slate-500 text-[11px] tracking-[0.2em] font-semibold uppercase">
                    {c.phone}
                  </div>

                  <a
                    href="tel:+33603199420"
                    className="mt-1 block text-[#0b1c3a] font-bold hover:text-[#0b3b8c] transition-colors"
                  >
                    +33 6 03 19 94 20
                  </a>

                  <a
                    href="tel:+33442072262"
                    className="block text-[#0b1c3a] font-bold hover:text-[#0b3b8c] transition-colors"
                  >
                    +33 4 42 07 22 62
                  </a>
                </div>
              </div>
            </div>

            {/* EMAIL */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </span>

                <div className="min-w-0">
                  <div className="text-slate-500 text-[11px] tracking-[0.2em] font-semibold uppercase">
                    {c.email}
                  </div>

                  <a
                    href="mailto:contact@gpefrance.eu"
                    className="mt-1 block text-[#0b1c3a] font-bold hover:text-[#0b3b8c] transition-colors break-all"
                  >
                    contact@gpefrance.eu
                  </a>
                </div>
              </div>
            </div>

            {/* ADDRESS */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </span>

                <div>
                  <div className="text-slate-500 text-[11px] tracking-[0.2em] font-semibold uppercase">
                    {c.address}
                  </div>

                  <div className="mt-1 text-[#0b1c3a] font-bold">
                    92 Bd de l’Europe ZA
                  </div>

                  <div className="text-slate-600 text-sm">
                    13127 Vitrolles, France
                  </div>
                </div>
              </div>
            </div>

            {/* HOURS */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </span>

                <div>
                  <div className="text-slate-500 text-[11px] tracking-[0.2em] font-semibold uppercase">
                    {c.hours}
                  </div>

                  <div className="mt-1 flex items-center justify-between gap-4 text-sm">
                    <span className="text-[#0b1c3a] font-semibold">
                      {c.weekdays}
                    </span>

                    <span className="text-slate-600">
                      08h00 – 18h00
                    </span>
                  </div>

                  <div className="mt-1 flex items-center justify-between gap-4 text-sm">
                    <span className="text-[#0b1c3a] font-semibold">
                      {c.weekend}
                    </span>

                    <span className="text-slate-600">
                      {c.closed}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* RESPONSE */}
            <div className="bg-[#0b1c3a] text-white rounded-3xl p-6 flex items-center gap-4">
              <span className="w-10 h-10 rounded-xl bg-[#f5c518] text-[#0b1c3a] flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </span>

              <div>
                <div className="font-bold text-sm">
                  {c.responseTitle}
                </div>

                <div className="text-white/70 text-xs mt-1">
                  {c.responseSub}
                </div>
              </div>
            </div>

            {/* SOCIAL */}
            <div className="flex items-center gap-3 pt-2">
              {[Linkedin, Facebook, Instagram].map((Ic, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 rounded-full bg-white border border-slate-200 text-[#0b1c3a] hover:bg-[#0b3b8c] hover:text-white hover:border-[#0b3b8c] flex items-center justify-center transition-colors"
                >
                  <Ic className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* RIGHT: FORM */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-10">

            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.formBadge}
            </span>

            <h2 className="mt-4 text-3xl md:text-4xl font-black text-[#0b1c3a] tracking-tight leading-tight">
              {c.formTitleA}
              <span className="italic font-serif text-[#0b3b8c]">
                {c.formTitleAccent}
              </span>
            </h2>

            <p className="mt-3 text-slate-600 text-sm">
              {c.formSub}
            </p>

            {sent && (
              <div className="mt-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5" />
                {c.success}
              </div>
            )}

            <form
              onSubmit={submit}
              className="mt-8 grid md:grid-cols-2 gap-5"
            >

              {/* INVISIBLE HONEYPOT */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                <label htmlFor="website">
                  Website
                </label>

                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={website}
                  onChange={(e) =>
                    setWebsite(e.target.value)
                  }
                />
              </div>

              {/* NAME */}
              <div className="md:col-span-1">
                <label className="block text-[11px] tracking-[0.2em] font-semibold text-slate-600 uppercase mb-2">
                  {c.fullName} *
                </label>

                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  placeholder={namePlaceholder}
                  autoComplete="name"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm outline-none focus:border-[#0b3b8c] focus:ring-2 focus:ring-[#0b3b8c]/15 transition-all"
                />
              </div>

              {/* EMAIL */}
              <div>
                <label className="block text-[11px] tracking-[0.2em] font-semibold text-slate-600 uppercase mb-2">
                  {c.emailLabel} *
                </label>

                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  placeholder={emailPlaceholder}
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm outline-none focus:border-[#0b3b8c] focus:ring-2 focus:ring-[#0b3b8c]/15 transition-all"
                />
              </div>

              {/* PHONE */}
              <div>
                <label className="block text-[11px] tracking-[0.2em] font-semibold text-slate-600 uppercase mb-2">
                  {c.phoneLabel}
                </label>

                <input
                  type="tel"
                  value={form.phone}
                  onChange={update('phone')}
                  placeholder={phonePlaceholder}
                  autoComplete="tel"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm outline-none focus:border-[#0b3b8c] focus:ring-2 focus:ring-[#0b3b8c]/15 transition-all"
                />
              </div>

              {/* COMPANY */}
              <div>
                <label className="block text-[11px] tracking-[0.2em] font-semibold text-slate-600 uppercase mb-2">
                  {c.company}
                </label>

                <input
                  type="text"
                  value={form.company}
                  onChange={update('company')}
                  placeholder={companyPlaceholder}
                  autoComplete="organization"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm outline-none focus:border-[#0b3b8c] focus:ring-2 focus:ring-[#0b3b8c]/15 transition-all"
                />
              </div>

              {/* SERVICE */}
              <div className="md:col-span-2">
                <label className="block text-[11px] tracking-[0.2em] font-semibold text-slate-600 uppercase mb-2">
                  {c.service}
                </label>

                <select
                  value={form.service}
                  onChange={update('service')}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm outline-none focus:border-[#0b3b8c] focus:ring-2 focus:ring-[#0b3b8c]/15 transition-all"
                >
                  <option value="">
                    {c.selectService}
                  </option>

                  {services.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.title}
                    </option>
                  ))}

                  <option value="other">
                    {c.otherRequest}
                  </option>
                </select>
              </div>

              {/* MESSAGE */}
              <div className="md:col-span-2">
                <label className="block text-[11px] tracking-[0.2em] font-semibold text-slate-600 uppercase mb-2">
                  {c.yourMessage} *
                </label>

                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={update('message')}
                  placeholder={c.messagePlaceholder}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm outline-none focus:border-[#0b3b8c] focus:ring-2 focus:ring-[#0b3b8c]/15 transition-all resize-none"
                />
              </div>

              {/* SUBMIT */}
              <div className="md:col-span-2">

                {error && (
                  <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#0b3b8c] hover:bg-[#082e6f] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm transition-colors shadow-md shadow-blue-900/20"
                >
                  {sending ? sendingLabel : c.send}
                  <Send className="w-4 h-4" />
                </button>

                <p className="mt-3 text-slate-500 text-xs text-center">
                  {c.policy}
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* MAP EMBED */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
            <iframe
              title="Localisation GPE - Vitrolles"
              src="https://www.openstreetmap.org/export/embed.html?bbox=5.2200%2C43.4560%2C5.2800%2C43.4800&amp;layer=mapnik&amp;marker=43.468%2C5.253"
              className="w-full h-[420px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
